// RegisterBusService.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  FiArrowLeft,
  FiSave,
  FiRefreshCw,
  FiTruck,
  FiUser,
  FiNavigation,
  FiHash,
  FiGlobe,
  FiMapPin,
  FiShuffle,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiInfo,
  FiFileText,
  FiSmartphone,
  FiMonitor
} from 'react-icons/fi';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  fetchOperators,
  fetchRoutePaths,
  registerNewBus,
  clearMessages,
  fetchStates,
  fetchZones,
  filterZonesByStateCode,
  checkBusNumberAvailability
} from '../../redux/Slices/busSlice';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BaseUrl from '../../Url/BaseUrl';

const RegisterBusService = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    operators,
    routePaths,
    zones,
    filteredZones,
    loading,
    errors,
    successMessage,
    checkingAvailability,
    search
  } = useSelector((state) => state.bus);

  const states = search?.states || [];

  const [formData, setFormData] = useState({
    busNo: '',
    busRegistrationNumber: '',
    busName: '',
    description: '',
    operatorNumber: '',
    routePathId: '',
    status: true,
    busContactNumber: [],
    stateCode: '',
    zoneCode: '',
    busType: 'Ordinary'
  });

  const [contactNumbers, setContactNumbers] = useState([{ dialCode: '91', number: '' }]);
  const [stands, setStands] = useState([]);
  const [groupedStands, setGroupedStands] = useState([]);
  // Timetable entries: { standCode, time, sortId }
  const [timetableEntries, setTimetableEntries] = useState([]);
  const [loadingRouteData, setLoadingRouteData] = useState(false);
  const [busNumberStatus, setBusNumberStatus] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const activeOperators = (operators || []).filter(op => op.status === true);
  const activeRoutePaths = (routePaths || []).filter(rp => rp.status === true);

  const sortedOperators = [...activeOperators].sort((a, b) =>
    (a.operatorName || '').localeCompare(b.operatorName || '')
  );
  const sortedRoutePaths = [...activeRoutePaths].sort((a, b) =>
    (a.routePathName || '').localeCompare(b.routePathName || '')
  );

  useEffect(() => {
    if (formData.busContactNumber && formData.busContactNumber.length > 0) {
      const numbers = formData.busContactNumber.map(contact => {
        const match = contact.match(/^\+(\d+)(.+)$/);
        if (match) {
          return { dialCode: match[1], number: match[2] };
        }
        return { dialCode: '91', number: contact };
      });
      setContactNumbers(numbers.length ? numbers : [{ dialCode: '91', number: '' }]);
    }
  }, []);

  useEffect(() => {
    const joined = contactNumbers
      .filter(contact => contact.number && contact.number.trim() !== '')
      .map(contact => `+${contact.dialCode}${contact.number}`);
    setFormData(prev => ({ ...prev, busContactNumber: joined }));
  }, [contactNumbers]);

  useEffect(() => {
    dispatch(fetchOperators());
    dispatch(fetchRoutePaths());
    dispatch(fetchStates());
    dispatch(fetchZones());

    return () => {
      dispatch(clearMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (formData.stateCode) {
      dispatch(filterZonesByStateCode(formData.stateCode));
      setFormData(prev => ({ ...prev, zoneCode: '' }));
    } else {
      dispatch(filterZonesByStateCode(''));
    }
  }, [formData.stateCode, dispatch]);

  useEffect(() => {
    if (!formData.routePathId) {
      setStands([]);
      setGroupedStands([]);
      setTimetableEntries([]);
      return;
    }

    const fetchStands = async () => {
      setLoadingRouteData(true);
      try {
        const standsRes = await axios.get(
          `${BaseUrl.baseurl}BusStands/GetBusStandsByRoutePathId?rpi=${formData.routePathId}`
        );
        let standsData = standsRes.data || [];

        standsData = standsData.filter(stand => stand.status === true);
        setStands(standsData);

        const villageMap = new Map();
        const orderedVillageCodes = [];

        standsData.forEach(stand => {
          const villageObj = stand.cityVillage || {};
          const villageCode = villageObj.cityVillageCode || stand.cityVillageCode || 'Unknown';
          let rawVillageName = villageObj.cityVillageName || stand.cityVillageName || '';

          let displayVillageName;
          if (rawVillageName && rawVillageName.trim() !== '' && rawVillageName !== villageCode) {
            displayVillageName = rawVillageName.trim();
          } else {
            displayVillageName = villageCode === 'Unknown' ? 'Unknown Area' : villageCode;
          }

          if (!villageMap.has(villageCode)) {
            villageMap.set(villageCode, {
              villageName: displayVillageName,
              stands: []
            });
            orderedVillageCodes.push(villageCode);
          }
          villageMap.get(villageCode).stands.push(stand);
        });

        const groupedArray = orderedVillageCodes.map(villageCode => {
          const group = villageMap.get(villageCode);
          const sortedStands = [...group.stands].sort((a, b) =>
            (a.busStandName || '').localeCompare(b.busStandName || '')
          );
          return {
            villageCode,
            villageName: group.villageName,
            stands: sortedStands
          };
        });

        setGroupedStands(groupedArray);
        setTimetableEntries([]);
      } catch (error) {
        console.error('Failed to fetch bus stands:', error);
        toast.error(t('failedToLoadRouteData', 'Failed to load route details.'));
      } finally {
        setLoadingRouteData(false);
      }
    };
    fetchStands();
  }, [formData.routePathId, t]);

  useEffect(() => {
    if (formData.busNo && busNumberStatus !== null) {
      setBusNumberStatus(null);
    }
  }, [formData.busNo]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage, {
        style: {
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb'
        }
      });
      dispatch(clearMessages());
    }
    const errorMessages = Object.values(errors).filter(e => e);
    if (errorMessages.length) {
      toast.error(errorMessages[0]);
      dispatch(clearMessages());
    }
  }, [successMessage, errors, dispatch]);

  const generateRandomBusNumber = async () => {
    let attempts = 0;
    const maxAttempts = 10;
    let newNumber = null;
    let isAvailable = false;

    while (attempts < maxAttempts && !isAvailable) {
      const min = 50000000;
      const max = 59999999;
      const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
      newNumber = randomNumber.toString();

      try {
        const response = await axios.get(`${BaseUrl.baseurl}Buses/CheckIfExists?id=${newNumber}`, {
          headers: { accept: 'text/plain' }
        });
        if (!response.data) {
          isAvailable = true;
          break;
        }
      } catch (err) {
        console.error('Error checking bus number existence:', err);
        isAvailable = true;
        break;
      }
      attempts++;
    }

    if (isAvailable && newNumber) {
      setFormData(prev => ({ ...prev, busNo: newNumber }));
      setBusNumberStatus('available');
      toast.success(t('busNumberGeneratedAndAvailable', 'Unique bus number generated and available!'), {
        style: {
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb'
        }
      });
    } else {
      toast.error(t('failedToGenerateUniqueNumber', 'Could not generate a unique bus number. Please try again or enter manually.'));
    }
  };

  const checkBusNumberAvailabilityHandler = async () => {
    if (!formData.busNo) {
      toast.error(t('enterBusNumberToCheck'));
      return;
    }
    if (!/^5\d{7}$/.test(formData.busNo)) {
      setBusNumberStatus('invalid');
      toast.error(t('busNumberFormatError'));
      return;
    }
    try {
      const response = await axios.get(`${BaseUrl.baseurl}Buses/CheckIfExists?id=${formData.busNo}`, {
        headers: { accept: 'text/plain' }
      });
      const exists = response.data;
      if (!exists) {
        setBusNumberStatus('available');
        toast.success(t('busNumberAvailable'), {
          style: {
            background: '#ffffff',
            color: '#1f2937',
            border: '1px solid #e5e7eb'
          }
        });
      } else {
        setBusNumberStatus('taken');
        toast.error(t('busNumberTaken'));
      }
    } catch (err) {
      console.error('Availability check failed:', err);
      toast.error(t('checkAvailabilityFailed'));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'busNo') {
      let numericValue = value.replace(/\D/g, '').slice(0, 8);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      if (busNumberStatus !== null) setBusNumberStatus(null);
    } else if (name === 'busRegistrationNumber') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === 'stateCode') {
      setFormData(prev => ({ ...prev, [name]: value, zoneCode: '' }));
    } else if (name === 'busType') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name !== 'busContactNumber') {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleContactNumberChange = (index, value) => {
    const updated = [...contactNumbers];
    updated[index] = { ...updated[index], number: value };
    setContactNumbers(updated);
    if (formErrors.busContactNumber) setFormErrors(prev => ({ ...prev, busContactNumber: '' }));
  };

  const handleDialCodeChange = (index, dialCode) => {
    const updated = [...contactNumbers];
    updated[index] = { ...updated[index], dialCode: dialCode };
    setContactNumbers(updated);
  };

  const addContactNumber = () => {
    setContactNumbers([...contactNumbers, { dialCode: '91', number: '' }]);
  };

  const removeContactNumber = (index) => {
    if (contactNumbers.length > 1) {
      const updated = contactNumbers.filter((_, i) => i !== index);
      setContactNumbers(updated);
    } else {
      setContactNumbers([{ dialCode: '91', number: '' }]);
    }
  };

  const handleStandToggle = (standCode) => {
    setTimetableEntries(prev => {
      const exists = prev.find(e => e.standCode === standCode);
      if (exists) {
        return prev.filter(e => e.standCode !== standCode);
      } else {
        return [...prev, { standCode, time: '', sortId: '' }];
      }
    });
  };

  const handleTimeChange = (standCode, time) => {
    setTimetableEntries(prev =>
      prev.map(e => (e.standCode === standCode ? { ...e, time: time || '' } : e))
    );
  };

  const handleSortIdChange = (standCode, sortId) => {
    // Only allow digits
    const numeric = sortId.replace(/\D/g, '');
    setTimetableEntries(prev =>
      prev.map(e => (e.standCode === standCode ? { ...e, sortId: numeric } : e))
    );
  };

  const validateBusForm = () => {
    const errors = {};
    if (!formData.stateCode) errors.stateCode = t('stateRequired');
    return errors;
  };

  const validateTimetable = () => {
    if (timetableEntries.length === 0) {
      toast.error(t('atLeastOneStand', 'Please select at least one bus stand.'));
      return false;
    }
    const missingArrival = timetableEntries.some(
      e => !e.time || e.time.trim() === ''
    );
    if (missingArrival) {
      toast.error(t('enterArrivalTime', 'Please enter arrival time for every selected stop.'));
      return false;
    }
    const missingSort = timetableEntries.some(
      e => e.sortId === '' || e.sortId === null || e.sortId === undefined
    );
    if (missingSort) {
      toast.error(t('enterSortNumber', 'Please enter sort number for every selected stop.'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (busNumberStatus !== 'available') {
      toast.error(t('busNumberNotAvailable', 'Please ensure bus number is available.'));
      return;
    }

    if (!validateTimetable()) {
      return;
    }

    const busTypeInt = formData.busType === 'NonStop' ? 1 : 0;

    const busObject = {
      busNo: parseInt(formData.busNo),
      busRegistrationNumber: formData.busRegistrationNumber,
      operatorNumber: formData.operatorNumber,
      routePathNo: formData.routePathId,
      busContactNumber: contactNumbers
        .filter(contact => contact.number && contact.number.trim() !== '')
        .map(contact => `+${contact.dialCode}${contact.number}`),
      status: formData.status,
      stateCode: formData.stateCode.toUpperCase(),
      zoneCode: formData.zoneCode.toUpperCase(),
      bType: busTypeInt
    };

    if (formData.busName && formData.busName.trim() !== '') {
      busObject.busName = formData.busName;
    }
    if (formData.description && formData.description.trim() !== '') {
      busObject.description = formData.description;
    }

    // Only arrivalTime and sortId — departureTime is null
    const busPayload = {
      bus: busObject,
      busStands: timetableEntries.map(entry => ({
        busStandCode: entry.standCode,
        arrivalTime: entry.time ? `${entry.time}:00` : null,
        departureTime: null,
        status: true,
        sortId: entry.sortId !== '' ? parseInt(entry.sortId, 10) : 0
      }))
    };

    console.log('=== BUS PAYLOAD (JSON) ===');
    console.log(JSON.stringify(busPayload, null, 2));
    console.log('========================');

    try {
      const response = await dispatch(registerNewBus(busPayload)).unwrap();

      console.log('=== SUCCESS RESPONSE (JSON) ===');
      console.log(JSON.stringify(response, null, 2));
      console.log('===============================');

      toast.success(t('busAndTimetableRegistered', 'Bus and timetable registered successfully!'), {
        className: 'bg-white text-gray-900 shadow-lg',
        style: {
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb'
        }
      });
      setTimeout(() => {
        navigate('/busfinderpanel');
      }, 10000);
    } catch (error) {
      console.error('=== REGISTRATION FAILED ===');
      console.error('Error object:', error);

      if (error.response) {
        console.log('Error Response:', JSON.stringify({
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        }, null, 2));
      } else if (error.request) {
        console.log('Error Request:', JSON.stringify({
          message: 'No response received',
          request: error.request
        }, null, 2));
      } else {
        console.log('Error Message:', JSON.stringify({
          name: error.name,
          message: error.message,
          stack: error.stack
        }, null, 2));
      }

      if (error.message?.toLowerCase().includes('cast') ||
          error.name === 'CastError' ||
          error.message?.includes('Cast to ObjectId failed')) {
        toast.error(t('dataTypeMismatch', 'Invalid data format. Please check IDs and numeric fields.'));
      }
      else if (error.name === 'ValidationError') {
        const validationErrors = {};
        for (const field in error.errors) {
          validationErrors[field] = error.errors[field].message;
        }
        console.log('=== VALIDATION ERRORS (JSON) ===');
        console.log(JSON.stringify(validationErrors, null, 2));
        toast.error(t('validationFailed', 'Please check all required fields.'));
      }
      else if (error.response) {
        const errorDetails = {
          statusCode: error.response.status,
          errorTitle: error.response.data?.title,
          errorMessage: error.response.data?.message,
          errorDetails: error.response.data?.errors,
          timestamp: new Date().toISOString()
        };
        console.log('=== SERVER ERROR (JSON) ===');
        console.log(JSON.stringify(errorDetails, null, 2));

        const errorMessage = error.response.data?.title ||
                            error.response.data?.message ||
                            t('registrationFailed');
        toast.error(errorMessage);
      }
      else if (error.request) {
        toast.error(t('networkError', 'Network error. Please check your connection.'));
      }
      else {
        toast.error(t('unexpectedError', 'An unexpected error occurred. Please try again.'));
      }
      console.log('===============================');
    }
  };

  const selectedOperator = operators.find(op => op.operatorNumber === formData.operatorNumber);
  const selectedRoute = routePaths.find(r => r.routePathNo === formData.routePathId);
  const selectedState = states.find(s => s.stateCode === formData.stateCode);
  const selectedZone = zones.find(z => z.zoneCode === formData.zoneCode);

  return (
    <div className="register-bus-page">
      <ToastContainer position="top-right" autoClose={3000} />

      <div>
        <div>
          <button className="btn-back" onClick={() => navigate('/busfinderpanel')}>
            ← Back
          </button>
        </div>
      </div>

      <div className="registration-layout">
        <div className="form-column">
          <form onSubmit={handleSubmit} className="bus-registration-form">
            <section className="form-card">
              <h2 className="card-title">
                <FiTruck className="title-icon" />
                {t('busInformation')}
              </h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>
                    <FiHash /> {t('busServiceNumberLabel', 'Bus Service Number')} <span className="required">*</span>
                  </label>
                  <div className="input-with-actions">
                    <input
                      type="text"
                      name="busNo"
                      value={formData.busNo}
                      onChange={handleChange}
                      className={`form-input ${formErrors.busNo ? 'error' : ''} ${
                        busNumberStatus === 'available' ? 'success' : ''
                      } ${busNumberStatus === 'taken' || busNumberStatus === 'invalid' ? 'error' : ''}`}
                      placeholder="e.g., 51234567"
                      maxLength="8"
                    />
                    <div className="input-actions">
                      <button type="button" className="btn-outline" onClick={generateRandomBusNumber}>
                        <FiShuffle /> {t('generate')}
                      </button>
                      <button type="button" className="btn-outline" onClick={checkBusNumberAvailabilityHandler}>
                        {checkingAvailability ? <FiRefreshCw className="spin" /> : <FiCheckCircle />}
                        {t('check')}
                      </button>
                    </div>
                  </div>
                  {busNumberStatus === 'available' && (
                    <div className="status-message success">
                      <FiCheckCircle /> {t('busNumberAvailableStatus')}
                    </div>
                  )}
                  {busNumberStatus === 'taken' && (
                    <div className="status-message error">
                      <FiAlertCircle /> {t('busNumberTakenStatus')}
                    </div>
                  )}
                  {busNumberStatus === 'invalid' && (
                    <div className="status-message error">
                      <FiAlertCircle /> {t('invalidFormatStatus')}
                    </div>
                  )}
                  {formErrors.busNo && <span className="field-error">{formErrors.busNo}</span>}
                  <small className="field-hint">{t('busNumberHint')}</small>
                </div>

                <div className="form-group">
                  <label>{t('rtoRegistrationLabel')} <span className="required">*</span></label>
                  <input
                    type="text"
                    name="busRegistrationNumber"
                    value={formData.busRegistrationNumber}
                    onChange={handleChange}
                    className={`form-input ${formErrors.busRegistrationNumber ? 'error' : ''}`}
                    placeholder=""
                  />
                  {formErrors.busRegistrationNumber && <span className="field-error">{formErrors.busRegistrationNumber}</span>}
                  <small className="field-hint">{t('rtoFormatHint')}</small>
                </div>

                <div className="form-group">
                  <label>{t('busServiceNameLabel', 'Bus Service Name')}</label>
                  <input
                    type="text"
                    name="busName"
                    value={formData.busName}
                    onChange={handleChange}
                    className={`form-input ${formErrors.busName ? 'error' : ''}`}
                    placeholder="e.g., Volvo AC"
                  />
                  {formErrors.busName && <span className="field-error">{formErrors.busName}</span>}
                </div>

                <div className="form-group">
                  <label>{t('busType', 'Bus Type')} <span className="required">*</span></label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="busType"
                        value="Ordinary"
                        checked={formData.busType === 'Ordinary'}
                        onChange={handleChange}
                      />
                      <span>{t('ordinary', 'Ordinary')}</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="busType"
                        value="NonStop"
                        checked={formData.busType === 'NonStop'}
                        onChange={handleChange}
                      />
                      <span>{t('nonStop', 'Non‑Stop')}</span>
                    </label>
                  </div>
                  {formErrors.busType && <span className="field-error">{formErrors.busType}</span>}
                </div>

                <div className="form-group full-width">
                  <label><FiFileText /> {t('description', 'Description')}</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="form-input"
                    rows="3"
                    placeholder={t('descriptionPlaceholder', 'Enter any additional details about the bus (amenities, type, notes, etc.)')}
                  />
                  <small className="field-hint">{t('descriptionHint', 'Optional: Provide extra information for passengers')}</small>
                </div>

                <div className="form-group full-width">
                  <label>{t('busContactLabel')}</label>
                  <div className="contact-numbers-container">
                    {contactNumbers.map((contact, index) => (
                      <div key={index} className="contact-number-row">
                        <div className="phone-input-wrapper">
                          <select
                            value={contact.dialCode}
                            onChange={(e) => handleDialCodeChange(index, e.target.value)}
                            className="country-code-select"
                          >
                            <option value="91">🇮🇳 +91 (India)</option>
                            <option value="1">🇺🇸 +1 (USA/Canada)</option>
                            <option value="44">🇬🇧 +44 (UK)</option>
                            <option value="61">🇦🇺 +61 (Australia)</option>
                            <option value="971">🇦🇪 +971 (UAE)</option>
                            <option value="65">🇸🇬 +65 (Singapore)</option>
                            <option value="60">🇲🇾 +60 (Malaysia)</option>
                            <option value="64">🇳🇿 +64 (New Zealand)</option>
                            <option value="49">🇩🇪 +49 (Germany)</option>
                            <option value="33">🇫🇷 +33 (France)</option>
                            <option value="81">🇯🇵 +81 (Japan)</option>
                            <option value="86">🇨🇳 +86 (China)</option>
                            <option value="82">🇰🇷 +82 (South Korea)</option>
                            <option value="966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="974">🇶🇦 +974 (Qatar)</option>
                            <option value="965">🇰🇼 +965 (Kuwait)</option>
                            <option value="968">🇴🇲 +968 (Oman)</option>
                            <option value="880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="977">🇳🇵 +977 (Nepal)</option>
                            <option value="92">🇵🇰 +92 (Pakistan)</option>
                            <option value="93">🇦🇫 +93 (Afghanistan)</option>
                            <option value="20">🇪🇬 +20 (Egypt)</option>
                            <option value="27">🇿🇦 +27 (South Africa)</option>
                            <option value="234">🇳🇬 +234 (Nigeria)</option>
                            <option value="254">🇰🇪 +254 (Kenya)</option>
                            <option value="55">🇧🇷 +55 (Brazil)</option>
                            <option value="52">🇲🇽 +52 (Mexico)</option>
                            <option value="34">🇪🇸 +34 (Spain)</option>
                            <option value="39">🇮🇹 +39 (Italy)</option>
                            <option value="7">🇷🇺 +7 (Russia)</option>
                            <option value="90">🇹🇷 +90 (Turkey)</option>
                          </select>
                          <input
                            type="tel"
                            value={contact.number}
                            onChange={(e) => handleContactNumberChange(index, e.target.value.replace(/\D/g, ''))}
                            className={`form-input ${formErrors.busContactNumber ? 'error' : ''}`}
                            placeholder={t('contactNumberPlaceholder', 'Enter mobile number')}
                            maxLength="15"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn-icon-small"
                          onClick={() => removeContactNumber(index)}
                          aria-label={t('removeNumber')}
                          disabled={contactNumbers.length === 1 && contact.number === ''}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn-outline btn-add-number" onClick={addContactNumber}>
                      + {t('AddNumber')}
                    </button>
                  </div>
                  {formErrors.busContactNumber && (
                    <span className="field-error">{formErrors.busContactNumber}</span>
                  )}
                  <small className="field-hint">{t('contactMultipleHint', 'Select country code and enter phone number (optional). You can add multiple.')}</small>
                </div>

                <div className="form-group">
                  <label><FiGlobe /> {t('stateLabel')} <span className="required">*</span></label>
                  <select
                    name="stateCode"
                    value={formData.stateCode}
                    onChange={handleChange}
                    className={`form-select ${formErrors.stateCode ? 'error' : ''}`}
                  >
                    <option value="">{t('selectStateOption')}</option>
                    {states.map(s => (
                      <option key={s.stateCode} value={s.stateCode}>{s.stateName} ({s.stateCode})</option>
                    ))}
                  </select>
                  {formErrors.stateCode && <span className="field-error">{formErrors.stateCode}</span>}
                </div>

                <div className="form-group">
                  <label><FiMapPin /> {t('zoneLabel')} <span className="required">*</span></label>
                  <select
                    name="zoneCode"
                    value={formData.zoneCode}
                    onChange={handleChange}
                    className={`form-select ${formErrors.zoneCode ? 'error' : ''}`}
                    disabled={!formData.stateCode}
                  >
                    <option value="">{t('selectZoneOption')}</option>
                    {filteredZones.map(z => (
                      <option key={z.zoneCode} value={z.zoneCode}>{z.zoneName} ({z.zoneCode})</option>
                    ))}
                  </select>
                  {formErrors.zoneCode && <span className="field-error">{formErrors.zoneCode}</span>}
                  {!formData.stateCode && <small className="field-hint">{t('selectStateFirst')}</small>}
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="status"
                      checked={formData.status}
                      onChange={handleChange}
                      className="form-checkbox"
                    />
                    <span>{t('activeService')}</span>
                  </label>
                </div>
              </div>
            </section>

            <section className="form-card">
              <h2 className="card-title">
                <FiUser className="title-icon" />
                {t('operatorInformation')}
              </h2>
              <div className="form-group">
                <label>{t('selectOperatorLabel')} <span className="required">*</span></label>
                <select
                  name="operatorNumber"
                  value={formData.operatorNumber}
                  onChange={handleChange}
                  className={`form-select ${formErrors.operatorNumber ? 'error' : ''}`}
                >
                  <option value="">{t('selectOperatorOption')}</option>
                  {sortedOperators.map(op => (
                    <option key={op.operatorNumber} value={op.operatorNumber}>
                      {op.operatorName} ({op.operatorNumber})
                    </option>
                  ))}
                </select>
                {formErrors.operatorNumber && <span className="field-error">{formErrors.operatorNumber}</span>}
                {selectedOperator && (
                  <div className="info-box">
                    <p><strong>{t('contact')}:</strong> {selectedOperator.busOperatorContactNumber}</p>
                    <p><strong>{t('androidAppLink')}:</strong> <a href={selectedOperator.appLink} target="_blank" rel="noopener noreferrer">{selectedOperator.appLink}</a></p>
                    <p><strong>{t('iosAppLink')}:</strong> <a href={selectedOperator.iOSAppLink} target="_blank" rel="noopener noreferrer">{selectedOperator.iOSAppLink}</a></p>
                    <p><strong>{t('website')}:</strong> <a href={selectedOperator.websiteContactLink} target="_blank" rel="noopener noreferrer">{selectedOperator.websiteContactLink}</a></p>
                  </div>
                )}
              </div>
            </section>

            <section className="form-card">
              <h2 className="card-title">
                <FiNavigation className="title-icon" />
                {t('routeInformation')}
              </h2>
              <div className="form-group">
                <label>{t('selectRoutePathLabel')} <span className="required">*</span></label>
                <select
                  name="routePathId"
                  value={formData.routePathId}
                  onChange={handleChange}
                  className={`form-select ${formErrors.routePathId ? 'error' : ''}`}
                >
                  <option value="">{t('selectRouteOption')}</option>
                  {sortedRoutePaths.map(r => (
                    <option key={r.routePathNo} value={r.routePathNo}>
                      {r.routePathName} ({r.routePathNo})
                    </option>
                  ))}
                </select>
                {formErrors.routePathId && <span className="field-error">{formErrors.routePathId}</span>}
                {selectedRoute && (
                  <div className="info-box">
                    <p><strong>{t('routeNumber')}:</strong> {selectedRoute.routeNo}</p>
                    <p><strong>{t('routeName')}:</strong> {selectedRoute.routePathName}</p>
                  </div>
                )}
              </div>
            </section>

            {formData.routePathId && (
              <section className="form-card timetable-card">
                <h2 className="card-title">
                  <FiClock className="title-icon" />
                  {t('defineStopsAndTimings', 'Define Stops & Timings')}
                </h2>
                {loadingRouteData ? (
                  <div className="loading-placeholder">
                    <FiRefreshCw className="spin" /> {t('loadingStops', 'Loading stops...')}
                  </div>
                ) : (
                  <>
                    {groupedStands.length === 0 ? (
                      <p className="empty-message">{t('noStandsFound', 'No active bus stands found for this route.')}</p>
                    ) : (
                      <div className="stands-container">
                        {groupedStands.map((group, groupIndex) => (
                          <div key={group.villageCode} className="village-group">
                            <div className="village-header">
                              <strong>{group.villageName}</strong> ({group.villageCode})
                            </div>
                            <div className="stands-grid">
                              {group.stands.map(stand => {
                                const entry = timetableEntries.find(e => e.standCode === stand.busStandCode);
                                const isChecked = !!entry;
                                return (
                                  <div key={stand.busStandCode} className="stand-item">
                                    <label className="stand-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleStandToggle(stand.busStandCode)}
                                      />
                                      <span className="stand-name">
                                        {stand.busStandName} ({stand.busStandCode})
                                      </span>
                                    </label>
                                    {isChecked && (
                                      <div className="time-inputs-group">
                                        <div className="time-field">
                                          <label>{t('arrivalTime', 'Arrival')}</label>
                                          <input
                                            type="time"
                                            value={entry.time}
                                            onChange={(e) => handleTimeChange(stand.busStandCode, e.target.value)}
                                            className="time-picker"
                                          />
                                        </div>
                                        <div className="time-field">
                                          <label>{t('sortNumber', 'Sort #')}</label>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            value={entry.sortId}
                                            onChange={(e) => handleSortIdChange(stand.busStandCode, e.target.value)}
                                            className="time-picker sort-input"
                                            placeholder="1"
                                            maxLength="4"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {groupIndex < groupedStands.length - 1 && <div className="village-separator"></div>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="stops-summary" style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#64748b' }}>
                      {t('stopsHint', 'Note: For each selected stop, provide arrival time and sort number. Sort number controls the order of stops.')}
                    </div>
                  </>
                )}
              </section>
            )}

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading.registration || checkingAvailability || loadingRouteData || busNumberStatus !== 'available'}
              >
                {loading.registration ? (
                  <><FiRefreshCw className="spin" /> {t('registering')}</>
                ) : (
                  <><FiSave /> {t('registerBusService')}</>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="preview-column">
          <div className="preview-card">
            <h2 className="preview-title">{t('registrationPreview')}</h2>
            <div className="preview-content">
              <div className="preview-section">
                <h3>{t('busDetails')}</h3>
                <dl>
                  <dt>{t('busServiceNumber', 'Bus Service No')}:</dt><dd>{formData.busNo || '—'}</dd>
                  <dt>{t('rtoRegistration')}:</dt><dd>{formData.busRegistrationNumber || '—'}</dd>
                  <dt>{t('busServiceName', 'Bus Service Name')}:</dt><dd>{formData.busName || '—'}</dd>
                  <dt>{t('busType', 'Bus Type')}:</dt><dd>{formData.busType || '—'}</dd>
                  <dt>{t('description', 'Description')}:</dt><dd>{formData.description || '—'}</dd>
                  <dt>{t('contactNumbers')}:</dt>
                  <dd>
                    {contactNumbers.filter(c => c.number && c.number.trim()).length > 0 ? (
                      <ul className="contact-preview-list">
                        {contactNumbers.filter(c => c.number && c.number.trim()).map((contact, idx) => (
                          <li key={idx}>+{contact.dialCode} {contact.number}</li>
                        ))}
                      </ul>
                    ) : '—'}
                  </dd>
                  <dt>{t('state')}:</dt><dd>{selectedState ? `${selectedState.stateName} (${selectedState.stateCode})` : '—'}</dd>
                  <dt>{t('zone')}:</dt><dd>{selectedZone ? `${selectedZone.zoneName} (${selectedZone.zoneCode})` : '—'}</dd>
                  <dt>{t('status')}:</dt><dd>{formData.status ? t('active') : t('inactive')}</dd>
                </dl>
              </div>
              <div className="preview-section">
                <h3>{t('operator')}</h3>
                {selectedOperator ? (
                  <dl>
                    <dt>{t('name')}:</dt><dd>{selectedOperator.operatorName}</dd>
                    <dt>{t('number')}:</dt><dd>{selectedOperator.operatorNumber}</dd>
                  </dl>
                ) : <p className="text-muted">{t('noOperatorSelected')}</p>}
              </div>
              <div className="preview-section">
                <h3>{t('route')}</h3>
                {selectedRoute ? (
                  <dl>
                    <dt>{t('routeName')}:</dt><dd>{selectedRoute.routePathName}</dd>
                    <dt>{t('routeNumber')}:</dt><dd>{selectedRoute.routeNo}</dd>
                  </dl>
                ) : <p className="text-muted">{t('noRouteSelected')}</p>}
              </div>
              <div className="preview-section">
                <h3>{t('stops', 'Stops')}</h3>
                {timetableEntries.length === 0 ? (
                  <p className="text-muted">{t('noStopsSelected')}</p>
                ) : (
                  <ul className="stops-list">
                    {[...timetableEntries]
                      .sort((a, b) => (parseInt(a.sortId || 0, 10) - parseInt(b.sortId || 0, 10)))
                      .map(e => {
                        // ✅ Find stand AND its village group
                        let stand = null;
                        let villageName = '';
                        for (const group of groupedStands) {
                          const found = group.stands.find(s => s.busStandCode === e.standCode);
                          if (found) {
                            stand = found;
                            villageName = group.villageName;
                            break;
                          }
                        }
                        return (
                          <li key={e.standCode} className="stop-item">
                            <div className="stop-left">
                              <span className="sort-badge">
                                #{e.sortId !== '' && e.sortId !== undefined ? e.sortId : '—'}
                              </span>
                              <div className="stop-info">
                                <span className="stop-name">
                                  {stand?.busStandName || e.standCode}
                                </span>
                                {villageName && (
                                  <span className="stop-village">
                                    <FiMapPin size={11} /> {villageName}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="stop-right">
                              {e.time ? (
                                <span className="stop-time">{e.time}</span>
                              ) : (
                                <span className="stop-time no-time">{t('noTime', 'No time')}</span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .register-bus-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem;
          font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          color: #1e293b;
          background: #f8fafc;
          min-height: 100vh;
        }
        .btn-back:hover {
          background: #f1f5f9;
        }
        .registration-layout {
          display: grid;
          grid-template-columns: 1fr 360px;
          gap: 1.5rem;
          align-items: start;
        }
        .form-column {
          background: transparent;
          min-width: 0;
        }
        .bus-registration-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .form-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          padding: 1.5rem;
          border: 1px solid #eef2f6;
        }
        .card-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0 0 1.25rem 0;
          color: #0f172a;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 0.75rem;
        }
        .title-icon {
          color: #2563eb;
        }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }
        .full-width {
          grid-column: span 2;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }
        .form-group label {
          font-weight: 500;
          font-size: 0.9rem;
          color: #334155;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        .required {
          color: #dc2626;
          margin-left: 2px;
        }
        .radio-group {
          display: flex;
          gap: 1.5rem;
          align-items: center;
          margin-top: 0.25rem;
        }
        .radio-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: normal;
          cursor: pointer;
        }
        .radio-label input[type="radio"] {
          width: 1rem;
          height: 1rem;
          accent-color: #2563eb;
          margin: 0;
        }
        .form-input, .form-select {
          padding: 0.6rem 0.75rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border 0.2s, box-shadow 0.2s;
          background: white;
          width: 100%;
          box-sizing: border-box;
        }
        .form-input:focus, .form-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        .form-input.error, .form-select.error {
          border-color: #dc2626;
          background-color: #fef2f2;
        }
        .form-input.success {
          border-color: #10b981;
          background-color: #f0fdf4;
        }
        .input-with-actions {
          display: flex;
          gap: 0.5rem;
        }
        .input-with-actions .form-input {
          flex: 1;
          min-width: 0;
        }
        .input-actions {
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0;
        }
        .btn-outline {
          padding: 0.5rem 1rem;
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #1e293b;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .btn-outline:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
        }
        .status-message {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.85rem;
          padding: 0.25rem 0;
        }
        .status-message.success {
          color: #10b981;
        }
        .status-message.error {
          color: #dc2626;
        }
        .field-error {
          color: #dc2626;
          font-size: 0.8rem;
        }
        .field-hint {
          color: #64748b;
          font-size: 0.75rem;
        }
        .checkbox-group {
          justify-content: center;
          margin-top: 0.5rem;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: normal;
        }
        .form-checkbox {
          width: 1.1rem;
          height: 1.1rem;
          accent-color: #2563eb;
        }
        .info-box {
          background: #f8fafc;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-top: 0.75rem;
          border-left: 4px solid #2563eb;
          font-size: 0.9rem;
        }
        .info-box p {
          margin: 0.25rem 0;
        }
        .info-box a {
          color: #2563eb;
          text-decoration: none;
          word-break: break-all;
        }
        .info-box a:hover {
          text-decoration: underline;
        }
        .timetable-card {
          background: #f0f9ff;
          border-left: 4px solid #2563eb;
        }
        .stands-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .village-group {
          margin-bottom: 0.5rem;
        }
        .village-header {
          font-size: 0.9rem;
          font-weight: 600;
          color: #1e40af;
          background: #e0f2fe;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          margin-bottom: 0.75rem;
        }
        .stands-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 0.75rem;
        }
        .stand-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: white;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .stand-checkbox {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 0.95rem;
          flex: 1;
          min-width: 120px;
        }
        .stand-checkbox input[type="checkbox"] {
          width: 1rem;
          height: 1rem;
          accent-color: #2563eb;
          flex-shrink: 0;
        }
        .stand-name {
          word-break: break-word;
        }
        .time-inputs-group {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          align-items: flex-end;
        }
        .time-field {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .time-field label {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .time-picker {
          padding: 0.25rem 0.5rem;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          font-size: 0.85rem;
          width: 110px;
          flex-shrink: 0;
        }
        .sort-input {
          width: 70px;
          text-align: center;
        }
        .village-separator {
          margin: 1rem 0 0.5rem;
          border-top: 2px dashed #cbd5e1;
        }
        .loading-placeholder {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #475569;
          padding: 1rem;
          justify-content: center;
        }
        .empty-message {
          color: #64748b;
          text-align: center;
          padding: 1rem;
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 0.5rem;
        }
        .btn-primary, .btn-secondary {
          padding: 0.75rem 1.5rem;
          border-radius: 40px;
          font-weight: 600;
          font-size: 0.95rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        .btn-primary {
          background: #2563eb;
          color: white;
          box-shadow: 0 4px 8px rgba(37, 99, 235, 0.2);
        }
        .btn-primary:hover:not(:disabled) {
          background: #1d4ed8;
          transform: translateY(-1px);
          box-shadow: 0 6px 12px rgba(37, 99, 235, 0.25);
        }
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-secondary {
          background: white;
          color: #334155;
          border: 1px solid #cbd5e1;
        }
        .btn-secondary:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
        }
        .preview-column {
          position: sticky;
          top: 1.5rem;
        }
        .preview-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
          border: 1px solid #eef2f6;
          overflow: hidden;
        }
        .preview-title {
          font-size: 1.2rem;
          font-weight: 600;
          padding: 1.25rem 1.5rem;
          margin: 0;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          color: #0f172a;
        }
        .preview-content {
          padding: 1.25rem 1.5rem 1.5rem;
        }
        .preview-section {
          margin-bottom: 1.5rem;
        }
        .preview-section h3 {
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #64748b;
          margin: 0 0 0.75rem 0;
          border-bottom: 1px dashed #e2e8f0;
          padding-bottom: 0.25rem;
        }
        .preview-section dl {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 0.25rem 0;
          font-size: 0.9rem;
          margin: 0;
        }
        .preview-section dt {
          color: #475569;
          font-weight: 500;
        }
        .preview-section dd {
          margin: 0;
          color: #0f172a;
          font-weight: 400;
        }
        .text-muted {
          color: #94a3b8;
          font-style: italic;
          margin: 0.25rem 0 0;
        }

        /* ---------- NEW STOPS PREVIEW UI ---------- */
        .stops-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .stop-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          padding: 0.65rem 0.75rem;
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #eef2f6;
          transition: all 0.2s ease;
        }
        .stop-item:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }
        .stop-left {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          min-width: 0;
          flex: 1;
        }
        .sort-badge {
          background: #2563eb;
          color: white;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 0.2rem 0.5rem;
          border-radius: 6px;
          flex-shrink: 0;
          min-width: 30px;
          text-align: center;
          letter-spacing: 0.3px;
        }
        .stop-info {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
          min-width: 0;
          flex: 1;
        }
        .stop-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.88rem;
          word-break: break-word;
          line-height: 1.3;
        }
        .stop-village {
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          color: #64748b;
          font-size: 0.72rem;
          font-style: italic;
          font-weight: 400;
        }
        .stop-right {
          flex-shrink: 0;
        }
        .stop-time {
          display: inline-block;
          color: #1e40af;
          background: #dbeafe;
          font-weight: 600;
          font-size: 0.8rem;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          white-space: nowrap;
        }
        .stop-time.no-time {
          color: #b91c1c;
          background: #fee2e2;
        }

        .contact-numbers-container {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .contact-number-row {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .phone-input-wrapper {
          flex: 1;
          display: flex;
          gap: 0.5rem;
        }
        .country-code-select {
          width: 140px;
          padding: 0.6rem 0.5rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.9rem;
          background: white;
          cursor: pointer;
        }
        .country-code-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        .contact-number-row .form-input {
          flex: 1;
        }
        .btn-icon-small {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          line-height: 1;
        }
        .btn-icon-small:hover {
          color: #dc2626;
          background: #fee2e2;
        }
        .btn-add-number {
          align-self: flex-start;
          margin-top: 0.25rem;
        }
        .contact-preview-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .contact-preview-list li {
          padding: 0.2rem 0;
          border-bottom: 1px dashed #e2e8f0;
        }
        @media (max-width: 1024px) {
          .registration-layout {
            grid-template-columns: 1fr 300px;
            gap: 1rem;
          }
          .form-grid {
            gap: 1rem;
          }
        }
        @media (max-width: 900px) {
          .registration-layout {
            grid-template-columns: 1fr;
          }
          .preview-column {
            position: static;
            margin-top: 1rem;
          }
          .preview-card {
            max-width: 600px;
            margin: 0 auto;
          }
        }
        @media (max-width: 768px) {
          .register-bus-page {
            padding: 0.75rem;
          }
          .form-card {
            padding: 1.25rem;
          }
          .form-grid {
            grid-template-columns: 1fr;
          }
          .full-width {
            grid-column: span 1;
          }
          .input-with-actions {
            flex-direction: column;
          }
          .input-actions {
            justify-content: stretch;
          }
          .input-actions .btn-outline {
            flex: 1;
            justify-content: center;
          }
          .stands-grid {
            grid-template-columns: 1fr;
          }
          .stand-item {
            flex-wrap: wrap;
          }
          .time-inputs-group {
            width: 100%;
          }
          .time-field {
            flex: 1;
          }
          .time-picker {
            width: 100%;
          }
          .form-actions {
            flex-direction: column-reverse;
          }
          .btn-primary, .btn-secondary {
            width: 100%;
            justify-content: center;
          }
          .phone-input-wrapper {
            flex-direction: column;
          }
          .country-code-select {
            width: 100%;
          }
          .village-header {
            font-size: 0.8rem;
          }
          /* Responsive preview stops */
          .stop-item {
            padding: 0.6rem 0.6rem;
            gap: 0.5rem;
          }
          .stop-name {
            font-size: 0.83rem;
          }
          .sort-badge {
            font-size: 0.68rem;
            padding: 0.18rem 0.4rem;
            min-width: 26px;
          }
          .stop-time {
            font-size: 0.75rem;
            padding: 0.2rem 0.5rem;
          }
          .stop-village {
            font-size: 0.68rem;
          }
        }
        @media (max-width: 400px) {
          .stop-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.4rem;
          }
          .stop-right {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
};

export default RegisterBusService;